import type { QuestionOption, WireChatMessage } from '@logistics/contracts';
import type { StepEvent } from './chat-format';

/** The question being answered right now, shown after the stored messages. */
export interface LiveTurn {
  readonly key: number;
  readonly conversationId: string | undefined;
  readonly user: WireChatMessage;
  readonly step?: StepEvent;
  readonly error?: string;
  /** The stored answer's id, once `done` has arrived. */
  readonly answered?: string;
}

export interface Ask {
  readonly text: string;
  readonly scope: readonly QuestionOption[];
  readonly attachments: WireChatMessage['attachments'];
}

/** Still waiting for the answer: the composer stays busy. */
export function isBusy(turn: LiveTurn | null): boolean {
  return turn !== null && turn.answered === undefined && turn.error === undefined;
}

/**
 * What of the live turn still has to be drawn after the stored messages: the
 * question until the cache holds it, the thinking dots until the answer is
 * stored, or the error.
 */
export function livePieces(
  turn: LiveTurn | null,
  messages: readonly WireChatMessage[],
): { user: boolean; thinking: boolean; error: string | undefined } {
  if (turn === null) return { user: false, thinking: false, error: undefined };
  const has = (id: string): boolean => messages.some((m) => m.id === id);
  const answered = turn.answered !== undefined && has(turn.answered);
  return {
    user: !answered && !has(turn.user.id),
    thinking: !answered && turn.error === undefined,
    error: turn.error,
  };
}

/**
 * Stored questions that never got an answer — the stream failed or was cut
 * off — by position. The last one is left to the live turn while one is
 * running or has just failed, which draws its own error.
 */
export function unansweredQuestions(
  messages: readonly WireChatMessage[],
  turn: LiveTurn | null,
): Set<number> {
  const found = new Set<number>();
  messages.forEach((m, i) => {
    if (m.role !== 'user' || messages[i + 1]?.role === 'agent') return;
    if (i === messages.length - 1 && turn !== null) return;
    found.add(i);
  });
  return found;
}
